import { useNavigate } from 'react-router-dom';
import { LoadingSection } from '../../common/LoadingSection';
import { useParams } from 'react-router';
import { useNotify } from 'react-admin';
import { NoDataSection } from '../../common/NoDataSection';
import { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Typography,
    Paper,
    Box,
    Dialog,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import useQuestionPaper from '../../hooks/useQuestionPaper';
import { goApi } from '../../services';
import SectionQuestionEdit from './SectionQuestionEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import QuestionBankEdit from '../questions_bank/QuestionBankEdit';

const QuestionListNew = () => {
    const navigate = useNavigate();
    const { testSeriesId, sectionId, testId, exerciseId } = useParams();
    const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
    const [editQuestionId, setEditQuestionId] = useState<string | null>(null);
    const notify = useNotify();
    const TestId = testId ? testId : (exerciseId as string);

    if (!TestId) {
        return <LoadingSection />;
    }
    const { questionPaper, loading, error, refetch } = useQuestionPaper(TestId, sectionId);
    const paper = Array.isArray(questionPaper) ? questionPaper[0] : questionPaper;
    const section = paper?.sections?.find((s: any) => s.id === sectionId) || paper?.sections?.[0];
    const questions = section?.questions || [];
    const SectionId = sectionId || section?.id || '';

    const handleEdit = (question: any) => {
        setEditQuestionId(question.id);
    };

    const handleDelete = async (id: string) => {
        try {
            await goApi.delete(`/sections-question-mappings/${SectionId}/question/${id}`);
            notify('Question deleted successfully', { type: 'success' });
            await refetch();
        } catch (err: any) {
            console.error('Error deleting question:', err);
            notify(err?.message || 'Failed to delete question', { type: 'error' });
        }
    };

    if (loading) {
        return <LoadingSection />;
    }

    if (error) {
        return (
            <Box sx={{ width: '100%', p: 2 }}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                    <Typography color="error" variant="h6">
                        Error loading questions
                    </Typography>
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {questions.length === 0 ? (
                <NoDataSection
                    resource={'questions'}
                    onCreate={() => {
                        const queryParams = new URLSearchParams();

                        if (testSeriesId) {
                            queryParams.set('testSeriesId', testSeriesId);
                        }
                        if (TestId) {
                            queryParams.set('testId', TestId);
                        }
                        if (SectionId) {
                            queryParams.set('sectionId', SectionId);
                        }
                        const queryString = queryParams.toString();
                        navigate(`/questions_bank/create${queryString ? `?${queryString}` : ''}`);
                    }}
                />
            ) : (
                <>
                    <div className="flex justify-end">
                        <Button
                            onClick={() => {
                                const queryParams = new URLSearchParams();
                                if (testSeriesId) {
                                    queryParams.set('testSeriesId', testSeriesId);
                                }
                                if (TestId) {
                                    queryParams.set('testId', TestId);
                                }
                                if (SectionId) {
                                    queryParams.set('sectionId', SectionId);
                                }
                                const queryString = queryParams.toString();
                                navigate(
                                    `/questions_bank/create${queryString ? `?${queryString}` : ''}`,
                                );
                            }}
                        >
                            <Add /> Add Question
                        </Button>
                    </div>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ border: '1px solid #e0e0e0', maxHeight: '80vh' }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                        Image
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                        Content
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                        Positive Marks
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                        Negative Marks
                                    </TableCell>
                                    <TableCell
                                        sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}
                                        align="center"
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {questions?.map((question: any) => (
                                    <TableRow
                                        key={question.id}
                                        hover
                                        sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                                    >
                                        <TableCell sx={{ width: 100 }}>
                                            {question?.image ? (
                                                <Box
                                                    component="img"
                                                    src={question?.image}
                                                    alt="Question"
                                                />
                                            ) : (
                                                <></>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Typography variant="body2">
                                                {question?.content}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {question?.positiveMarks}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 400 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {question?.negativeMarks}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Edit />}
                                                    onClick={() => setSelectedQuestion(question)}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    Edit Section Question
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Edit />}
                                                    onClick={() => handleEdit(question)}
                                                    sx={{ fontSize: '12px' }}
                                                >
                                                    Edit Question
                                                </Button>

                                                <DeleteIcon
                                                    onClick={() => handleDelete(question.id)}
                                                    fontSize="small"
                                                    sx={{ color: 'red', cursor: 'pointer' }}
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div>
                        <p className="flex justify-end py-4">Total Question: {questions?.length}</p>
                    </div>
                </>
            )}

            <Dialog
                open={!!selectedQuestion}
                onClose={() => setSelectedQuestion(null)}
                maxWidth="md"
                fullWidth
            >
                {selectedQuestion && (
                    <SectionQuestionEdit
                        sectionId={SectionId}
                        question={selectedQuestion}
                        onClose={() => setSelectedQuestion(null)}
                    />
                )}
            </Dialog>

            <Dialog
                open={!!editQuestionId}
                onClose={() => setEditQuestionId(null)}
                maxWidth="lg"
                fullWidth
            >
                {editQuestionId && (
                    <Box sx={{ p: 2 }}>
                        <QuestionBankEdit
                            questionId={editQuestionId}
                            onClose={() => setEditQuestionId(null)}
                        />
                    </Box>
                )}
            </Dialog>
        </Box>
    );
};

export default QuestionListNew;
